import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { 
  ShoppingCart, 
  ArrowDownToLine, 
  Search, 
  User, 
  Building2, 
  CreditCard,
  Banknote,
  Smartphone,
  CheckCircle2,
  AlertCircle,
  Loader2,
  IndianRupee
} from 'lucide-react';

const PAYMENT_MODES = [
  { value: 'M', label: 'Net Banking', icon: Building2, description: 'Direct debit from bank' },
  { value: 'UPI', label: 'UPI', icon: Smartphone, description: 'Pay via UPI apps' },
  { value: 'NEFT', label: 'NEFT', icon: Banknote, description: 'Bank transfer (T+1)' },
  { value: 'RTGS', label: 'RTGS', icon: Banknote, description: 'Real-time transfer' },
  { value: 'DC', label: 'Debit Card', icon: CreditCard, description: 'Card payment' },
];

export const TransactionsPage = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('purchase');
  
  // Purchase form state
  const [purchaseForm, setPurchaseForm] = useState({
    amount: '',
    paymentMode: 'M',
    folioNumber: ''
  });
  
  // Redemption form state
  const [redemptionForm, setRedemptionForm] = useState({
    folioNumber: '',
    units: '',
    amount: '',
    redeemAll: false,
    bankAccount: ''
  });
  
  // Client holdings for redemption
  const [clientHoldings, setClientHoldings] = useState([]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bse/clients?status=registered`);
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const searchFunds = async (query) => {
    if (!query || query.length < 2) {
      setFunds([]);
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/funds/search?q=${encodeURIComponent(query)}`);
      setFunds(response.data.funds || []);
    } catch (error) {
      console.error('Error searching funds:', error);
      toast.error('Failed to search funds');
    } finally {
      setSearching(false);
    }
  };

  const fetchClientHoldings = async (clientId) => {
    try {
      const client = clients.find(c => c.client_id === clientId);
      if (client?.ucc_code) {
        const response = await axios.get(`${API}/bse/portfolio/${client.ucc_code}`);
        setClientHoldings(response.data.holdings || []);
      }
    } catch (error) {
      console.error('Error fetching holdings:', error);
      setClientHoldings([]);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    setSelectedClient(client);
    if (activeTab === 'redemption') {
      fetchClientHoldings(clientId);
    }
  };

  const handleFundSelect = (fund) => {
    setSelectedFund(fund);
    setFunds([]);
    setSearchQuery(fund.schemeName);
  };

  const handlePurchaseSubmit = async () => {
    if (!selectedClient || !selectedFund || !purchaseForm.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (parseFloat(purchaseForm.amount) < 100) {
      toast.error('Minimum purchase amount is ₹100');
      return;
    }
    
    setShowConfirmDialog(true);
  };

  const confirmPurchase = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bse/orders/purchase`, {
        ucc_code: selectedClient.ucc_code,
        scheme_code: selectedFund.schemeCode,
        amount: parseFloat(purchaseForm.amount),
        payment_mode: purchaseForm.paymentMode,
        folio_number: purchaseForm.folioNumber || null
      });
      
      if (response.data.status === 'success') {
        toast.success(`Order placed! Order ID: ${response.data.order_id}`);
        if (response.data.payment_url) {
          window.open(response.data.payment_url, '_blank');
        }
      } else {
        toast.success('Order saved. Payment pending.');
      }
      
      // Reset form
      setSelectedFund(null);
      setSearchQuery('');
      setPurchaseForm({ amount: '', paymentMode: 'M', folioNumber: '' });
      setShowConfirmDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRedemptionSubmit = async () => {
    if (!selectedClient || !redemptionForm.folioNumber) {
      toast.error('Please select client and folio');
      return;
    }
    
    if (!redemptionForm.redeemAll && !redemptionForm.units && !redemptionForm.amount) {
      toast.error('Please enter units or amount to redeem');
      return;
    }
    
    setSubmitting(true);
    try {
      const holding = clientHoldings.find(h => h.folio_number === redemptionForm.folioNumber);
      
      const response = await axios.post(`${API}/bse/orders/redemption`, {
        ucc_code: selectedClient.ucc_code,
        scheme_code: holding?.scheme_code || '',
        folio_number: redemptionForm.folioNumber,
        units: redemptionForm.units ? parseFloat(redemptionForm.units) : null,
        amount: redemptionForm.amount ? parseFloat(redemptionForm.amount) : null,
        all_units: redemptionForm.redeemAll,
        bank_account_number: selectedClient.bank_accounts?.[0]?.account_number || ''
      });
      
      toast.success(`Redemption order placed! Order ID: ${response.data.order_id}`);
      
      // Reset form
      setRedemptionForm({ folioNumber: '', units: '', amount: '', redeemAll: false, bankAccount: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place redemption order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="transactions-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Place Order</h1>
          <p className="text-muted-foreground mt-1">Execute purchase or redemption orders for your clients</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card/50 border border-border/50">
            <TabsTrigger value="purchase" className="gap-2">
              <ShoppingCart className="w-4 h-4" />
              Purchase
            </TabsTrigger>
            <TabsTrigger value="redemption" className="gap-2">
              <ArrowDownToLine className="w-4 h-4" />
              Redemption
            </TabsTrigger>
          </TabsList>

          {/* Purchase Tab */}
          <TabsContent value="purchase" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Step 1: Select Client */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</div>
                    Select Client
                  </CardTitle>
                  <CardDescription>Choose a registered client</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select 
                    value={selectedClient?.client_id || ''} 
                    onValueChange={handleClientSelect}
                  >
                    <SelectTrigger data-testid="select-client">
                      <SelectValue placeholder="Select client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.length === 0 ? (
                        <SelectItem value="none" disabled>No registered clients</SelectItem>
                      ) : (
                        clients.map(client => (
                          <SelectItem key={client.client_id} value={client.client_id}>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              <span>{client.first_name} {client.last_name}</span>
                              <span className="text-muted-foreground text-xs">({client.pan})</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  
                  {selectedClient && (
                    <div className="mt-4 p-3 bg-background/50 rounded-lg space-y-1">
                      <p className="font-medium">{selectedClient.first_name} {selectedClient.last_name}</p>
                      <p className="text-sm text-muted-foreground">PAN: {selectedClient.pan}</p>
                      <p className="text-sm text-muted-foreground">UCC: {selectedClient.ucc_code || 'Pending'}</p>
                      <p className="text-sm text-muted-foreground">Email: {selectedClient.email}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Step 2: Select Fund */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</div>
                    Select Fund
                  </CardTitle>
                  <CardDescription>Search and select mutual fund</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search fund by name..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        searchFunds(e.target.value);
                      }}
                      className="pl-10"
                      data-testid="search-fund"
                    />
                    {searching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                    )}
                  </div>
                  
                  {/* Fund search results */}
                  {funds.length > 0 && (
                    <div className="mt-2 max-h-60 overflow-y-auto border border-border/50 rounded-lg">
                      {funds.map(fund => (
                        <button
                          key={fund.schemeCode}
                          className="w-full text-left p-3 hover:bg-muted/50 border-b border-border/30 last:border-0"
                          onClick={() => handleFundSelect(fund)}
                        >
                          <p className="font-medium text-sm">{fund.schemeName}</p>
                          <p className="text-xs text-muted-foreground">{fund.schemeCode}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {selectedFund && (
                    <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">{selectedFund.schemeName}</p>
                          <p className="text-xs text-muted-foreground">Code: {selectedFund.schemeCode}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Step 3: Enter Amount & Payment */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</div>
                    Amount & Payment
                  </CardTitle>
                  <CardDescription>Enter investment details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="amount">Investment Amount (₹) *</Label>
                    <div className="relative mt-1">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="amount"
                        type="number"
                        placeholder="10000"
                        value={purchaseForm.amount}
                        onChange={(e) => setPurchaseForm({...purchaseForm, amount: e.target.value})}
                        className="pl-10"
                        min="100"
                        data-testid="input-amount"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Minimum: ₹100</p>
                  </div>
                  
                  <div>
                    <Label>Payment Mode *</Label>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {PAYMENT_MODES.slice(0, 4).map(mode => {
                        const Icon = mode.icon;
                        return (
                          <button
                            key={mode.value}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              purchaseForm.paymentMode === mode.value
                                ? 'border-primary bg-primary/10'
                                : 'border-border/50 hover:border-border'
                            }`}
                            onClick={() => setPurchaseForm({...purchaseForm, paymentMode: mode.value})}
                          >
                            <Icon className="w-4 h-4 mb-1" />
                            <p className="text-sm font-medium">{mode.label}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="folio">Folio Number (Optional)</Label>
                    <Input
                      id="folio"
                      placeholder="For additional purchase"
                      value={purchaseForm.folioNumber}
                      onChange={(e) => setPurchaseForm({...purchaseForm, folioNumber: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={handlePurchaseSubmit}
                    disabled={!selectedClient || !selectedFund || !purchaseForm.amount || submitting}
                    data-testid="submit-purchase"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ShoppingCart className="w-4 h-4 mr-2" />
                    )}
                    Place Purchase Order
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Redemption Tab */}
          <TabsContent value="redemption" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Select Client & Holding */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Select Client & Holding</CardTitle>
                  <CardDescription>Choose client and fund to redeem</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Select Client *</Label>
                    <Select 
                      value={selectedClient?.client_id || ''} 
                      onValueChange={handleClientSelect}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select client..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(client => (
                          <SelectItem key={client.client_id} value={client.client_id}>
                            {client.first_name} {client.last_name} ({client.pan})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedClient && (
                    <div>
                      <Label>Select Holding *</Label>
                      {clientHoldings.length === 0 ? (
                        <div className="mt-2 p-4 bg-muted/50 rounded-lg text-center">
                          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">No holdings found for this client</p>
                        </div>
                      ) : (
                        <div className="mt-2 space-y-2">
                          {clientHoldings.map(holding => (
                            <button
                              key={holding.folio_number}
                              className={`w-full p-3 rounded-lg border text-left transition-all ${
                                redemptionForm.folioNumber === holding.folio_number
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border/50 hover:border-border'
                              }`}
                              onClick={() => setRedemptionForm({...redemptionForm, folioNumber: holding.folio_number})}
                            >
                              <p className="font-medium text-sm">{holding.scheme_name}</p>
                              <div className="flex justify-between mt-1">
                                <span className="text-xs text-muted-foreground">Units: {holding.units?.toFixed(3)}</span>
                                <span className="text-xs text-muted-foreground">Value: ₹{holding.market_value?.toLocaleString()}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">Folio: {holding.folio_number}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Redemption Details */}
              <Card className="bg-card/50 backdrop-blur border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">Redemption Details</CardTitle>
                  <CardDescription>Enter units or amount to redeem</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="redeemAll"
                      checked={redemptionForm.redeemAll}
                      onChange={(e) => setRedemptionForm({
                        ...redemptionForm, 
                        redeemAll: e.target.checked,
                        units: '',
                        amount: ''
                      })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="redeemAll">Redeem All Units</Label>
                  </div>
                  
                  {!redemptionForm.redeemAll && (
                    <>
                      <div>
                        <Label htmlFor="units">Units to Redeem</Label>
                        <Input
                          id="units"
                          type="number"
                          placeholder="Enter units"
                          value={redemptionForm.units}
                          onChange={(e) => setRedemptionForm({...redemptionForm, units: e.target.value, amount: ''})}
                          className="mt-1"
                          step="0.001"
                        />
                      </div>
                      
                      <div className="text-center text-muted-foreground">- OR -</div>
                      
                      <div>
                        <Label htmlFor="redeem-amount">Amount to Redeem (₹)</Label>
                        <Input
                          id="redeem-amount"
                          type="number"
                          placeholder="Enter amount"
                          value={redemptionForm.amount}
                          onChange={(e) => setRedemptionForm({...redemptionForm, amount: e.target.value, units: ''})}
                          className="mt-1"
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Redemption proceeds will be credited to the registered bank account within 3-4 working days.
                    </p>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={handleRedemptionSubmit}
                    disabled={!selectedClient || !redemptionForm.folioNumber || submitting}
                    data-testid="submit-redemption"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowDownToLine className="w-4 h-4 mr-2" />
                    )}
                    Place Redemption Order
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Confirmation Dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Purchase Order</DialogTitle>
              <DialogDescription>Please review your order details</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Client</p>
                  <p className="font-medium">{selectedClient?.first_name} {selectedClient?.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">UCC</p>
                  <p className="font-medium">{selectedClient?.ucc_code || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground">Fund</p>
                <p className="font-medium">{selectedFund?.schemeName}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Amount</p>
                  <p className="font-medium text-xl">₹{parseFloat(purchaseForm.amount || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Mode</p>
                  <p className="font-medium">{PAYMENT_MODES.find(m => m.value === purchaseForm.paymentMode)?.label}</p>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                Cancel
              </Button>
              <Button onClick={confirmPurchase} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirm Order
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TransactionsPage;
